import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Youtube } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Logo URLs
  const mainLogo = "/ASPR_ACDMY_16YRS.png";
  const cambridgeLogo = "/cambridge-logo.png";
  const edexcelLogo = "/edexcel-logo.png";

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'IGCSE Programs', href: '/igcse' },
    { name: 'A Level Programs', href: '/a-levels' },
    { name: 'Results', href: '/results' },
    { name: 'Contact', href: '/contact' },
  ];

  const igcseSubjects = [
    { name: 'Mathematics', href: '/igcse/igcse-mathematics' },
    { name: 'Physics', href: '/igcse/igcse-physics' },
    { name: 'Chemistry', href: '/igcse/igcse-chemistry' },
    { name: 'Biology', href: '/igcse/igcse-biology' },
    { name: 'Business', href: '/igcse/igcse-business' },
  ];

  const aLevelSubjects = [
    { name: 'Mathematics', href: '/a-levels/a-level-mathematics-as' },
    { name: 'Physics', href: '/a-levels/a-level-physics-as' },
    { name: 'Chemistry', href: '/a-levels/a-level-chemistry-as' },
    { name: 'Economics', href: '/a-levels/a-level-economics-as' },
    { name: 'Accounting', href: '/a-levels/a-level-accounting-as' },
  ];

  return (
    <footer className="bg-charcoal text-white pb-16 sm:pb-0" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-main py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <img 
                src={mainLogo} 
                alt="Aspire Academy" 
                className="h-15 w-auto"
              />
            </Link>
            <p className="text-gray-400 text-base leading-relaxed mb-6">
              Sri Lanka's premier institution for Cambridge and Edexcel IGCSE & A Level tuition. 
              Dedicated to academic excellence since 2015.
            </p>
            {/* Exam Board Logos */}
            <div className="flex items-center gap-4 mb-6">
              <img src={cambridgeLogo} alt="Cambridge" className="h-8 w-auto" />
              <img src={edexcelLogo} alt="Edexcel" className="h-12 w-auto" />
            </div>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 bg-white/10 hover:bg-aspire-red flex items-center justify-center transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 hover:bg-aspire-red flex items-center justify-center transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 hover:bg-aspire-red flex items-center justify-center transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6">IGCSE Subjects</h4>
            <ul className="space-y-3 mb-8">
              {igcseSubjects.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-heading font-bold text-lg mb-6">A Level Subjects</h4>
            <ul className="space-y-3">
              {aLevelSubjects.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-aspire-red flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  102/5A Srimath Anagarika Mw,<br />
                  Colombo 07, Sri Lanka
                </span>
              </li>
              <li>
                <a href="tel:+94743652899" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 text-aspire-red flex-shrink-0" />
                  <span className="text-sm">+94 74 365 2899</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@aspireacademy.lk" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-aspire-red flex-shrink-0" />
                  <span className="text-sm">info@aspireacademy.lk</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/94743652899" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-sm font-medium mt-2 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp Us
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-main py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Aspire Academy. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-500 hover:text-white text-sm transition-colors">Terms of Service</Link>
              <Link to="/return-policy" className="text-gray-500 hover:text-white text-sm transition-colors">Return Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
